/* eslint-disable no-restricted-syntax */
import { BaseResponse } from '@utils/types/shared/base';

/**
 * Session status types for the unified operations view
 * Based on backend API: processing, queued, uploaded, error, finished, streaming, stranded
 */
export type SessionStatus =
  | 'uploading'
  | 'processing'
  | 'streaming'
  | 'finished'
  | 'error'
  | 'stranded'
  | 'queued';

/**
 * Session mode (batch processing vs real-time streaming)
 */
export type SessionMode = 'decode' | 'align' | 'upload';

/**
 * GPU Autoscale Config from backend
 */
export type GpuAutoscaleConfig = {
  min_replicas?: number;
  max_replicas?: number;
  current_replicas?: number;
  target_replicas?: number;
  scale_up_threshold?: number;
  scale_down_threshold?: number;
} | null;

/**
 * System health metrics from backend API
 */
export type SystemHealth = {
  queue_depth: number;
  queue_processing: number;
  queue_queued: number;
  // Of queue_queued: work that will be dequeued in the normal flow
  // (auto-managed orgs, priority <= queue_priority_threshold) vs work parked
  // behind the threshold (throttled/deprioritised orgs). At capacity the
  // parked bucket can wait many hours, so a queue_depth driven by it is a
  // throttling backlog, not a processing stall.
  queue_queued_actionable: number;
  queue_queued_deprioritized: number;
  queue_priority_threshold: number;
  queue_failed: number;
  queue_dead_letter: number;
  // Median (p50) end-to-end minutes over the last 24h; p90 alongside so the
  // tail stays visible. Was a mean, which one very long file could dominate.
  processing_speed_minutes: number;
  processing_p90_minutes?: number;
  processing_sample_size?: number;
  // Median turnaround / audio duration. 0.33 = a 10-min file done in ~3m20s.
  processing_ratio?: number;
  error_count_24h: number;
  stranded_count: number;
  stuck_processing_count: number;
  gpu_config: GpuAutoscaleConfig;
};

/**
 * Individual session in the operations view
 * Based on backend API response format
 */
export type OperationsSession = {
  id: number;
  session_id: string;
  organization_id: number;
  organization_name: string;
  status: string;
  duration: number | null;
  language: string;
  filename: string | null;
  date_session_start: string;
  /** Arrival time in our system. date_session_start is partly customer-supplied
   * and routinely sits in the future or days in the past, so it cannot answer
   * "what showed up most recently" — this is what the list sorts by. */
  created_at: string;
  last_update: string;
  session_mode: SessionMode;
  error_message: string | null;
  error_category: string | null;
  error_code: string | null;
  error_reason: string | null;
  queue_status: string | null;
  priority: number | null;
  retry_count: number | null;
  is_stuck: boolean;
  is_stranded: boolean;
};

/**
 * Session counts by status
 */
export type SessionCounts = {
  total: number;
  processing: number;
  queued: number;
  uploaded: number;
  error: number;
  finished: number;
  stranded: number;
  streaming: number;
};

/**
 * Response from the unified operations API
 * GET /api/admin/operations/live
 */
export type OperationsLiveResponse = {
  count: number;
  total_results: number;
  system_health: SystemHealth;
  session_counts: SessionCounts;
  result: OperationsSession[];
};

/**
 * Wrapped response type
 */
export type OperationsLiveResponseData = BaseResponse<OperationsLiveResponse>;

/**
 * WebSocket message for session updates
 */
export type SessionUpdate = {
  type: 'session_update';
  data: {
    session_id: string;
    status: SessionStatus;
    updated_at: string;
  };
};

/**
 * WebSocket message for health updates
 */
export type HealthUpdate = {
  type: 'health_update';
  data: Partial<SystemHealth>;
};

/**
 * Bulk action types
 */
export type BulkAction = 'requeue' | 'restart' | 'kill' | 'cleanup';

/**
 * Bulk action request payload
 */
export type BulkActionRequest = {
  session_ids: string[];
  action: BulkAction;
};

/**
 * Individual action result in bulk response
 */
export type BulkActionResult = {
  success: boolean;
  message: string;
  session_id: string;
};

/**
 * Bulk action response
 */
export type BulkActionResponse = {
  success: boolean;
  message: string;
  affected_count: number;
  results: BulkActionResult[];
};

/**
 * Priority change request
 */
export type PriorityChangeRequest = {
  priority: number;
};

/**
 * Session action response
 */
export type SessionActionResponse = {
  success: boolean;
  message: string;
  session_id: string;
};

/**
 * Time range presets for operations filtering
 */
export type TimeRangePreset = '24h' | '7d' | '30d' | 'all' | 'custom';

/**
 * Query parameters for operations list
 */
export type OperationsQueryParams = {
  status?: string;
  search?: string;
  /** Case-insensitive partial match on session filename */
  filename?: string;
  /** Case-insensitive partial match on session ID */
  session_id?: string;
  /** Case-insensitive partial match on organization name */
  organization_name?: string;
  /** Filter sessions with duration >= value (seconds) */
  min_duration?: number;
  organization_id?: number;
  limit?: number;
  offset?: number;
  sort?:
    | 'created_at'
    | 'date_session_start'
    | 'status'
    | 'organization'
    | 'duration'
    | 'session_id'
    | 'filename';
  direction?: 'asc' | 'desc';
  /** Preset time window - defaults to '24h' on backend */
  time_range?: TimeRangePreset;
  /** Custom range start (ISO 8601) - overrides time_range when provided */
  date_from?: string;
  /** Custom range end (ISO 8601) - overrides time_range when provided */
  date_to?: string;
};

/**
 * Status filter options for UI
 */
export const SESSION_STATUS_OPTIONS: SessionStatus[] = [
  'queued',
  'processing',
  'streaming',
  'finished',
  'error',
  'stranded'
];

/**
 * Status color mapping for UI
 */
export const STATUS_COLORS: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  uploading: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3B82F6', label: 'blue' },
  uploaded: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3B82F6', label: 'blue' },
  queued: { bg: 'rgba(156, 163, 175, 0.15)', text: '#6B7280', label: 'gray' },
  processing: {
    bg: 'rgba(234, 179, 8, 0.15)',
    text: '#EAB308',
    label: 'yellow'
  },
  streaming: {
    bg: 'rgba(168, 85, 247, 0.15)',
    text: '#A855F7',
    label: 'purple'
  },
  finished: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22C55E', label: 'green' },
  error: { bg: 'rgba(239, 68, 68, 0.15)', text: '#EF4444', label: 'red' },
  stranded: { bg: 'rgba(249, 115, 22, 0.15)', text: '#F97316', label: 'orange' }
};
